import { duration } from "common";
import dayjs from "dayjs";
import { useEffect } from "react";
import { InfiniteData } from "react-query";
import { Purchase, PurchaseItemObject } from "types";
import { Page } from "./helper";
import { useInfiniteCollection } from "./useInfiniteCollection";
import { queryClient } from "@/api/queryClient";
import { updateCollectionByQueryKey } from "@/helpers/cacheHelper";

const QUERY_KEY = "purchaseNotifications";
const POLLING_INTERVAL = duration(5, "minutes");
const PAGE_SIZE = 100;
export const MAX_PURCHASE_NOTIFICATIONS = 200;

interface PurchaseNotifications {
    purchases: Purchase[];
    hasNextPage: boolean;
    isFetchingNextPage: boolean;
    fetchNextPage: () => void;
}

export const usePurchaseNotifications = (): PurchaseNotifications => {
    const { data, hasNextPage, isFetching, isFetchingNextPage, isError, fetchNextPage } =
        useInfiniteCollection<Purchase>(
            "purchases",
            {
                "notifyAt[lte]": dayjs().toISOString(),
                "items.object[nin]": `${PurchaseItemObject.GIFT},${PurchaseItemObject.STORE_CREDIT}`,
                "balance[notEqual]": 0,
                sort: "notifyAt",
                limit: PAGE_SIZE,
            },
            {
                queryKey: QUERY_KEY,
                notifyOnChangeProps: ["data", "hasNextPage", "isFetching", "isFetchingNextPage", "isError"],
                refetchInterval: POLLING_INTERVAL,
            },
        );

    // The API pages oldest-first but the drawer re-sorts by due date and the badge counts what is loaded,
    // so any unloaded page hides the newest notifications. Not `queryItemLimit`: it empties pages on refetch.
    // Waits out any fetch, since `fetchNextPage` cancels an in-flight refetch by default. Paused while `isError`:
    // retrying a failed page here would loop with no backoff; the next poll clears it.
    useEffect(() => {
        if (hasNextPage && !isFetching && !isError && data.length < MAX_PURCHASE_NOTIFICATIONS) {
            fetchNextPage();
        }
    }, [data.length, hasNextPage, isError, isFetching, fetchNextPage]);

    return { purchases: data, hasNextPage: hasNextPage ?? false, isFetchingNextPage, fetchNextPage };
};

// Not `updateCache` with insert: it appends the purchase to every page that lacks it, duplicating rows.
export const updatePurchaseNotificationsCache = (purchase: Purchase) => {
    const cache = queryClient.getQueryData<InfiniteData<Page<Purchase>>>(QUERY_KEY);
    const isCached = cache?.pages.some((page) => page.data.some(({ id }) => id === purchase.id));

    // Stops at the first page that changes, so the purchase is replaced or added exactly once
    updateCollectionByQueryKey<Purchase>(QUERY_KEY, (purchases) =>
        isCached ? purchases.map((item) => (item.id === purchase.id ? purchase : item)) : [...purchases, purchase],
    );
};
